/** @jsx jsx */
import { Box, Paragraph } from "@eworkit/iris";
import { FC } from "react";
import { unstable_CompositeItemWidget as CompositeItemWidget } from "reakit/Composite";
import { jsx } from "theme-ui";

import { TimeReportProjectItemAggregate } from "@/data/time-reports";

import { getTimeReportCalendarCellBackground } from "./grid/TimeReportCalendarCellBackground";
import { useTimeReportCalendarGrid } from "./grid/TimeReportCalendarGridContext";
import { TimeReportCalendarRowCell } from "./grid/TimeReportCalendarRowCell";
import { TimeReportCalendarPayCodeRowCellStatus } from "./TimeReportCalendarPayCodeRowCellStatus";
import { TimeReportCalendarPayCodeRowCellValidation } from "./TimeReportCalendarPayCodeRowCellValidation";
import { useTimeReportCalendarItemCell } from "./useTimeReportCalendarItemCell";

interface TimeReportCalendarPayCodeRowCellProps {
  item: TimeReportProjectItemAggregate;
}

export const TimeReportCalendarPayCodeRowCell: FC<TimeReportCalendarPayCodeRowCellProps> = ({ item, ...restProps }) => {
  const { isEditable, compositeState } = useTimeReportCalendarGrid();

  const { value, status, isValid, validationError, itemStatus, handleChange, handleFocus, handleBlur, handleKeyDown } =
    useTimeReportCalendarItemCell(item);

  return (
    <TimeReportCalendarRowCell
      {...restProps}
      border
      background={isEditable && !item.loggedTime.isEmpty ? "g50" : getTimeReportCalendarCellBackground(item)}
      sx={{
        "&:focus > input, &:focus-within > input": {
          display: "block",
        },
      }}>
      {isEditable && item.isEditable && (
        <CompositeItemWidget
          {...compositeState}
          as="input"
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={itemStatus !== undefined && itemStatus !== "idle"}
          sx={{
            position: "absolute",
            top: 1,
            bottom: 0,
            left: 0,
            right: 1,

            px: 16,
            pb: 3,
            cursor: "default",
            fontFamily: "body",
            fontSize: 14,

            display: "none",
            opacity: 0,

            "&:focus": {
              opacity: 1,
            },
          }}
        />
      )}

      {item.loggedTime.reportedTime > 0 && <Paragraph>{item.loggedTime.formattedAmount}</Paragraph>}

      {isEditable && item.isEditable && item.loggedTime.reportedTime === 0 && (
        <Paragraph color="n500">{item.loggedTime.formattedAmount}</Paragraph>
      )}

      {status === "focus" && !isValid && (
        <Box sx={{ position: "absolute", bottom: -27, left: 0, width: "max-content", zIndex: "tooltip" }}>
          <TimeReportCalendarPayCodeRowCellValidation measureUnit={item.measureUnit} error={validationError} />
        </Box>
      )}

      <TimeReportCalendarPayCodeRowCellStatus status={itemStatus} />
    </TimeReportCalendarRowCell>
  );
};
